/**
 * Wigglitz Lock Modal
 * Handles product/tag locking with code entry and email subscription unlock methods.
 */

(function() {
  'use strict';

  var config = window.wigglitzLockConfig || {
    lockedTags: '',
    unlockCode: '',
    klaviyoListId: '',
    storageKey: 'wigglitz-lock-unlocked'
  };

  var lockedTagsArray = [];
  if (config.lockedTags) {
    lockedTagsArray = config.lockedTags.split(',').map(function(tag) {
      return tag.trim().toLowerCase();
    }).filter(function(tag) {
      return tag.length > 0;
    });
  }

  // Pending action to execute after unlock
  var pendingAction = null;

  /**
   * Check if products are unlocked
   */
  function isUnlocked() {
    try {
      return localStorage.getItem(config.storageKey) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Set unlocked state
   */
  function setUnlocked() {
    try {
      localStorage.setItem(config.storageKey, 'true');
      document.body.classList.add('lock-unlocked');
      updateLockUI();
    } catch (e) {
      console.warn('Could not save unlock state to localStorage');
    }
  }

  /**
   * Check if a tag is locked
   */
  function isTagLocked(tag) {
    if (!tag) return false;
    var normalizedTag = tag.trim().toLowerCase();
    return lockedTagsArray.indexOf(normalizedTag) !== -1;
  }

  /**
   * Open the lock modal
   */
  function openModal() {
    var modal = document.getElementById('lock-modal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Focus on the first input
      var firstInput = modal.querySelector('.lock-modal__input');
      if (firstInput) {
        setTimeout(function() {
          firstInput.focus();
        }, 100);
      }
    }
  }

  /**
   * Close the lock modal
   */
  function closeModal() {
    var modal = document.getElementById('lock-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';

      // Reset form states
      var codeError = modal.querySelector('.js-lock-code-error');
      var emailError = modal.querySelector('.js-lock-email-error');
      var emailSuccess = modal.querySelector('.js-lock-email-success');
      var codeInput = modal.querySelector('.js-lock-code-input');
      var emailInput = modal.querySelector('.js-lock-email-input');

      if (codeError) codeError.style.display = 'none';
      if (emailError) emailError.style.display = 'none';
      if (emailSuccess) emailSuccess.style.display = 'none';
      if (codeInput) codeInput.value = '';
      if (emailInput) emailInput.value = '';
    }
  }

  /**
   * Validate unlock code
   */
  function validateCode(inputCode) {
    if (!config.unlockCode) return false;
    return inputCode.trim().toLowerCase() === config.unlockCode.trim().toLowerCase();
  }

  /**
   * Handle code form submission
   */
  function handleCodeSubmit(e) {
    e.preventDefault();

    var input = document.querySelector('.js-lock-code-input');
    var errorEl = document.querySelector('.js-lock-code-error');

    if (!input) return;

    var code = input.value;

    if (validateCode(code)) {
      setUnlocked();
      closeModal();
      executePendingAction();
    } else {
      if (errorEl) {
        errorEl.style.display = 'block';
      }
      input.focus();
      input.select();
    }
  }

  /**
   * Handle email form submission via Klaviyo API
   */
  function handleEmailSubmit(e) {
    e.preventDefault();

    var form = e.target;
    var emailInput = form.querySelector('.js-lock-email-input');
    var submitButton = form.querySelector('.lock-modal__button');
    var successEl = form.querySelector('.js-lock-email-success');
    var errorEl = form.querySelector('.js-lock-email-error');
    var originalButtonText = submitButton ? submitButton.textContent : '';

    if (!emailInput || !emailInput.value) return;

    if (!config.klaviyoListId) {
      console.warn('Klaviyo List ID not configured');
      setUnlocked();
      closeModal();
      executePendingAction();
      return;
    }

    // Hide any previous messages
    if (errorEl) errorEl.style.display = 'none';
    if (successEl) successEl.style.display = 'none';

    // Disable button during submission
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    // Subscribe via Klaviyo AJAX endpoint
    fetch('https://a.klaviyo.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        g: config.klaviyoListId,
        email: emailInput.value,
        $source: 'Locked Products Unlock'
      }).toString()
    })
    .then(function(response) {
      if (response.ok) {
        setUnlocked();
        if (successEl) {
          successEl.style.display = 'block';
        }
        // Close modal after short delay
        setTimeout(function() {
          closeModal();
          executePendingAction();
        }, 1500);
      } else {
        throw new Error('Subscription failed');
      }
    })
    .catch(function(error) {
      console.warn('Klaviyo subscription error:', error);
      if (errorEl) {
        errorEl.style.display = 'block';
      }
      // Re-enable button on error
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    });
  }

  /**
   * Execute pending action after unlock
   */
  function executePendingAction() {
    if (pendingAction) {
      if (pendingAction.type === 'navigate') {
        window.location.href = pendingAction.url;
      } else if (pendingAction.type === 'filter' && pendingAction.element) {
        // Re-trigger the filter click
        pendingAction.element.click();
      }
      pendingAction = null;
    }
  }

  /**
   * Switch between tabs
   */
  function switchTab(tabName) {
    var tabs = document.querySelectorAll('.js-lock-tab');
    var panels = document.querySelectorAll('.lock-modal__panel');

    tabs.forEach(function(tab) {
      if (tab.getAttribute('data-tab') === tabName) {
        tab.classList.add('lock-modal__tab--active');
      } else {
        tab.classList.remove('lock-modal__tab--active');
      }
    });

    panels.forEach(function(panel) {
      if (panel.id === 'lock-panel-' + tabName) {
        panel.style.display = 'block';
        panel.classList.add('lock-modal__panel--active');

        // Focus input
        var input = panel.querySelector('.lock-modal__input');
        if (input) {
          setTimeout(function() {
            input.focus();
          }, 100);
        }
      } else {
        panel.style.display = 'none';
        panel.classList.remove('lock-modal__panel--active');
      }
    });
  }

  /**
   * Update UI after unlock
   */
  function updateLockUI() {
    // Remove lock overlays
    var overlays = document.querySelectorAll('.product-block__lock-overlay');
    overlays.forEach(function(overlay) {
      overlay.style.display = 'none';
    });

    // Remove locked class
    var lockedProducts = document.querySelectorAll('.product-block--locked');
    lockedProducts.forEach(function(product) {
      product.classList.remove('product-block--locked');
    });

    // Remove lock icons from filter tags
    var lockIcons = document.querySelectorAll('.product-tags__item--locked .lock-icon');
    lockIcons.forEach(function(icon) {
      icon.style.display = 'none';
    });

    // Remove locked class from filter tags
    var lockedTags = document.querySelectorAll('.product-tags__item--locked');
    lockedTags.forEach(function(tag) {
      tag.classList.remove('product-tags__item--locked');
      tag.removeAttribute('data-locked');
    });
  }

  /**
   * Intercept clicks on locked product cards
   */
  function interceptProductCardClicks() {
    document.addEventListener('click', function(e) {
      // Already unlocked
      if (isUnlocked()) return;

      // Check if click is on a locked product link or overlay
      var lockedOverlay = e.target.closest('.product-block__lock-overlay');
      var lockedProduct = e.target.closest('.product-block--locked');

      if (lockedOverlay || lockedProduct) {
        var productLink = lockedProduct ? lockedProduct.querySelector('a[href]') : null;

        // Check if the actual clicked element is a link within the locked product
        var clickedLink = e.target.closest('a[href]');
        if (clickedLink && lockedProduct && lockedProduct.contains(clickedLink)) {
          e.preventDefault();
          e.stopPropagation();

          pendingAction = {
            type: 'navigate',
            url: clickedLink.href
          };

          openModal();
        } else if (lockedOverlay) {
          e.preventDefault();
          e.stopPropagation();

          if (productLink) {
            pendingAction = {
              type: 'navigate',
              url: productLink.href
            };
          }

          openModal();
        }
      }
    }, true);
  }

  /**
   * Initialize modal event listeners
   */
  function initModalEvents() {
    // Close button clicks
    document.querySelectorAll('.js-lock-modal-close').forEach(function(el) {
      el.addEventListener('click', closeModal);
    });

    // Tab switching
    document.querySelectorAll('.js-lock-tab').forEach(function(tab) {
      tab.addEventListener('click', function() {
        switchTab(this.getAttribute('data-tab'));
      });
    });

    // Code form submission
    var codeForm = document.querySelector('.js-lock-code-form');
    if (codeForm) {
      codeForm.addEventListener('submit', handleCodeSubmit);
    }

    // Email form submission
    var emailForm = document.querySelector('.js-lock-email-form');
    if (emailForm) {
      emailForm.addEventListener('submit', handleEmailSubmit);
    }

    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        var modal = document.getElementById('lock-modal');
        if (modal && modal.style.display === 'flex') {
          closeModal();
        }
      }
    });

    // Clear error on input
    var codeInput = document.querySelector('.js-lock-code-input');
    if (codeInput) {
      codeInput.addEventListener('input', function() {
        var errorEl = document.querySelector('.js-lock-code-error');
        if (errorEl) {
          errorEl.style.display = 'none';
        }
      });
    }
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Check if already unlocked
    if (isUnlocked()) {
      document.body.classList.add('lock-unlocked');
      updateLockUI();
    }

    initModalEvents();
    interceptProductCardClicks();
  }

  // Expose functions globally for use in custom.js
  window.wigglitzLockIsUnlocked = isUnlocked;
  window.wigglitzLockOpenModal = openModal;
  window.wigglitzLockSetPendingAction = function(action) {
    pendingAction = action;
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
